import { TFunction } from 'i18next';

import { Log } from '@/shared/types/analytics';
import { parseTimestamp } from '@/util/dateUtils';

/**
 * Calculates goal completion progress metadata for a session.
 *
 * @param goalAchieved - Goal status flag returned by analytics
 * @param t - Translation function
 * @returns Progress bar value and tooltip text
 */
export function getGoalPercentage(goalAchieved: string, t: TFunction) {
  if (goalAchieved === '1') {
    return { value: 100, tooltip: t('analytics.goal_achieved_tooltip') };
  }
  if (goalAchieved === '2') {
    return { value: 60, tooltip: t('analytics.goal_partial_achieved_tooltip') };
  }
  return { value: 10, tooltip: t('analytics.goal_not_achieved_tooltip') };
}

/**
 * Formats a session's date and time information
 * Uses parseTimestamp to ensure UTC timestamps without timezone info are handled correctly
 *
 * @param startTime - ISO string of session start time
 * @returns Object containing formatted date and time strings in local timezone
 */
export function formatSessionDateTime(startTime: string) {
  const date = parseTimestamp(startTime);
  if (!date) {
    return { date: '', time: '' };
  }
  return { date: date.toLocaleDateString(), time: date.toLocaleTimeString() };
}

/**
 * Calculates session duration in minutes
 * Uses parseTimestamp to ensure UTC timestamps without timezone info are handled correctly
 *
 * @param startTime - ISO string of session start time
 * @param endTime - ISO string of session end time
 * @returns Duration in minutes, rounded to nearest integer
 */
export function calculateSessionDuration(startTime: string, endTime: string): number {
  const start = parseTimestamp(startTime);
  const end = parseTimestamp(endTime);
  if (!start || !end) {
    return 0;
  }
  return Math.round((end.getTime() - start.getTime()) / 1000 / 60);
}

/**
 * Formats session duration with appropriate units
 * Uses parseTimestamp to ensure UTC timestamps without timezone info are handled correctly
 *
 * @param startTime - ISO string of session start time
 * @param endTime - ISO string of session end time
 * @param t - Translation function
 * @returns Object containing formatted duration value and unit
 */
export function formatDuration(
  startTime: string,
  endTime: string,
  t: TFunction
): { value: number; unit: string } {
  const start = parseTimestamp(startTime);
  const end = parseTimestamp(endTime);
  if (!start || !end) {
    return { value: 0, unit: t('common.sec_plural', { count: 0 }) };
  }
  const durationInSeconds = Math.round((end.getTime() - start.getTime()) / 1000);
  const durationInMinutes = Math.round(durationInSeconds / 60);

  // Show seconds if duration is 0 minutes or less
  if (durationInMinutes <= 0) {
    const seconds = Math.max(0, durationInSeconds);
    return { value: seconds, unit: t('common.sec_plural', { count: seconds }) };
  }

  // Show minutes for 1-59 minutes
  if (durationInMinutes < 60) {
    return { value: durationInMinutes, unit: t('common.min_plural', { count: durationInMinutes }) };
  }

  // Show hours for 60-1439 minutes (1-23 hours)
  if (durationInMinutes < 1440) {
    const hours = Math.round(durationInMinutes / 60);
    return { value: hours, unit: t('common.hour_plural', { count: hours }) };
  }

  // Show days for 1440+ minutes (24+ hours)
  const days = Math.round(durationInMinutes / 1440);
  return { value: days, unit: t('common.day_plural', { count: days }) };
}

/**
 * Maps sentiment values to their display properties
 *
 * @param sentiment - Sentiment value ('1', '2', '3', or null)
 * @param t - Translation function
 * @returns Object with tooltip text and icon variant
 */
export function getSentimentDisplay(sentiment: string | null, t: TFunction) {
  const sentimentMap = {
    '1': { tooltip: t('analytics.positive_sentiment'), variant: 'happy' as const },
    '2': { tooltip: t('analytics.neutral_sentiment'), variant: 'neutral' as const },
    '3': { tooltip: t('analytics.negative_sentiment'), variant: 'sad' as const },
  };

  return sentimentMap[sentiment as keyof typeof sentimentMap] || null;
}

/**
 * Determines if a session is contained and covered
 *
 * @param session - Log session object
 * @returns Object with coverage and containment status
 */
export function getSessionAttributes(session: Log) {
  return {
    isCovered: session.is_covered,
    isContained: session.is_contained,
    channel: session.channel,
  };
}

/**
 * Formats user display name from session data
 *
 * @param session - Log session object
 * @returns User display name or fallback value
 */
export function getUserDisplayName(session: Log): string {
  return session.user_name || session.user_id || '-';
}
